import { WaypointType } from '@/types/waypoint';

// ─── Custom SVG icon imports (Vite ?raw — returns file content as string) ─────
import foodSourceSvg from '@/assets/icons/map/food-source.svg?raw';
import trailCameraSvg from '@/assets/icons/map/trail-camera.svg?raw';
import waterSourceSvg from '@/assets/icons/map/water-source.svg?raw';
import gameTrailSvg from '@/assets/icons/map/game-trail.svg?raw';
import beddingAreaSvg from '@/assets/icons/map/bedding-area.svg?raw';
import accessPointSvg from '@/assets/icons/map/access-point.svg?raw';
import blindSvg from '@/assets/icons/map/blind.svg?raw';
import observationPointSvg from '@/assets/icons/map/observation-point.svg?raw';
import genericMarkerSvg from '@/assets/icons/map/generic-marker.svg?raw';
import propertySvg from '@/assets/icons/map/property.svg?raw';

// ─── Marker color palette (intentionally separate from bark/ember UI colors) ──
// High-contrast, saturated colors that read against satellite, topo, and vector tiles.
export const MARKER_COLORS: Record<WaypointType, string> = {
  [WaypointType.FoodSource]: '#22C55E',
  [WaypointType.Camera]: '#F59E0B',
  [WaypointType.WaterSource]: '#3B82F6',
  [WaypointType.GameTrail]: '#D97706',
  [WaypointType.BeddingArea]: '#A855F7',
  [WaypointType.AccessPoint]: '#94A3B8',
  [WaypointType.Blind]: '#84CC16',
  [WaypointType.ObservationPoint]: '#38BDF8',
  [WaypointType.Other]: '#9CA3AF',
};

// ─── SVG registry — maps each WaypointType to its custom SVG asset ─────────────
const WAYPOINT_SVG_MAP: Record<WaypointType, string> = {
  [WaypointType.FoodSource]: foodSourceSvg,
  [WaypointType.Camera]: trailCameraSvg,
  [WaypointType.WaterSource]: waterSourceSvg,
  [WaypointType.GameTrail]: gameTrailSvg,
  [WaypointType.BeddingArea]: beddingAreaSvg,
  [WaypointType.AccessPoint]: accessPointSvg,
  [WaypointType.Blind]: blindSvg,
  [WaypointType.ObservationPoint]: observationPointSvg,
  [WaypointType.Other]: genericMarkerSvg,
};

/** Raw SVG string for the property map marker (use in property-level icons). */
export const PROPERTY_ICON_SVG: string = propertySvg;

/** Returns the raw SVG string (white stroke, no size attrs) for a waypoint type. */
export function getWaypointIconSvg(type: WaypointType): string {
  return WAYPOINT_SVG_MAP[type] ?? genericMarkerSvg;
}

// ─── Build a sized SVG string for insertion into HTML ─────────────────────────

function buildIconSvg(rawSvg: string, size: number, strokeColor?: string): string {
  let svg = rawSvg.replace('<svg ', `<svg width="${size}" height="${size}" `);
  if (strokeColor) {
    svg = svg.replace('stroke="white"', `stroke="${strokeColor}"`);
  }
  return svg;
}

// ─── Zoom-dependent marker sizes ──────────────────────────────────────────────

export interface MarkerSize {
  shape: 'circle' | 'teardrop';
  /** For circle: diameter. For teardrop: width of the body. */
  width: number;
  /** For circle: same as width. For teardrop: total height including tip. */
  height: number;
  showIcon: boolean;
  showLabel: boolean;
}

export function getMarkerSize(zoom: number): MarkerSize {
  if (zoom >= 18) return { shape: 'teardrop', width: 32, height: 44, showIcon: true, showLabel: true };
  if (zoom >= 15) return { shape: 'teardrop', width: 26, height: 36, showIcon: true, showLabel: false };
  if (zoom >= 13) return { shape: 'teardrop', width: 20, height: 28, showIcon: true, showLabel: false };
  return { shape: 'circle', width: 14, height: 14, showIcon: false, showLabel: false };
}

/**
 * Returns 'bottom' for teardrop zoom levels (13+), 'center' for circle dot zoom levels.
 * Use this as the `anchor` option when creating MapLibre markers.
 */
export function getMarkerAnchor(zoom: number): 'center' | 'bottom' {
  return zoom >= 13 ? 'bottom' : 'center';
}

/**
 * Returns a sized SVG string for the icon inside a marker.
 * strokeColor defaults to 'white' for use on colored map markers.
 * Pass a hex color string to recolor the icon (e.g., for the TypePickerSheet).
 *
 * Exported so the map legend (WaypointIconCircle) and type picker can reuse the same icons.
 */
export function iconSvgPath(type: WaypointType, iconSize: number, strokeColor?: string): string {
  const rawSvg = getWaypointIconSvg(type);
  return buildIconSvg(rawSvg, iconSize, strokeColor);
}

// ─── Marker icon descriptor (replaces L.DivIcon — same .options shape for test compat) ──

export interface MarkerIconDescriptor {
  options: {
    html: string;
    className: string;
    iconSize: [number, number];
    iconAnchor: [number, number];
    popupAnchor: [number, number];
  };
}

// ─── Teardrop path — viewBox "0 0 30 40" ────────────────────────────────────
// Circle center in viewBox: (15, 14), radius 13. Tip at (15, 40) — bottom of viewBox
// so that anchor:'bottom' on the MapLibre Marker aligns the tip exactly to the coordinate.
const TEARDROP_PATH = 'M15 40 C15 40 2 22 2 14 A13 13 0 1 1 28 14 C28 22 15 40 15 40Z';

// Icon center in viewBox coordinates: (15, 14). Scale: width/30, height/40.
// iconCenterY in element = 14 * (height/40)
// iconTop = iconCenterY - iconSize/2
// iconLeft = width/2 - iconSize/2

function buildTeardropHtml(
  type: WaypointType,
  width: number,
  height: number,
  showIcon: boolean,
  showLabel: boolean,
  label: string | undefined,
): string {
  const color = MARKER_COLORS[type];

  // Icon sizing: ~50% of teardrop width, positioned at circle center
  const iconSize = Math.round(width * 0.5);
  const iconCenterY = 14 * (height / 40);
  const iconTop = iconCenterY - iconSize / 2;
  const iconLeft = width / 2 - iconSize / 2;

  const iconHtml = showIcon
    ? `<div style="position:absolute; top:${iconTop}px; left:${iconLeft}px; width:${iconSize}px; height:${iconSize}px; display:flex; align-items:center; justify-content:center; pointer-events:none;">${iconSvgPath(type, iconSize)}</div>`
    : '';

  // SAFETY: label is a user-supplied waypoint name — it is HTML-escaped before insertion.
  const safeLabel =
    label != null
      ? label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      : '';

  const labelHtml =
    showLabel && label
      ? `<span style="position:absolute; top:${height + 3}px; left:50%; transform:translateX(-50%); white-space:nowrap; font-family:'DM Sans',system-ui,sans-serif; font-size:10px; font-weight:600; color:#f0ede8; background:rgba(28,26,23,0.85); padding:1px 5px; border-radius:3px; pointer-events:none; box-shadow:0 1px 3px rgba(0,0,0,0.5);">${safeLabel}</span>`
      : '';

  return `<div style="position:relative; width:${width}px; height:${height}px; filter:drop-shadow(0 1px 3px rgba(0,0,0,0.6))">
  <svg width="${width}" height="${height}" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
    <path d="${TEARDROP_PATH}" fill="${color}" stroke="rgba(255,255,255,0.85)" stroke-width="2.5"/>
  </svg>
  ${iconHtml}
  ${labelHtml}
</div>`;
}

/**
 * Returns a marker icon descriptor for a given waypoint type and zoom level.
 * The returned object has the same `.options` shape as a Leaflet DivIcon so
 * existing tests that check `icon.options.iconSize` and `icon.options.html`
 * continue to pass without modification.
 *
 * Use `createMarkerElement()` to get a live HTMLElement for MapLibre markers.
 */
export function createWaypointDivIcon(
  type: WaypointType,
  zoom: number,
  label?: string,
): MarkerIconDescriptor {
  const { shape, width, height, showIcon, showLabel } = getMarkerSize(zoom);
  const color = MARKER_COLORS[type];

  if (shape === 'teardrop') {
    const html = buildTeardropHtml(type, width, height, showIcon, showLabel, label);

    // iconAnchor: bottom tip = [width/2, height]
    // popupAnchor: above pin = [0, -(height - iconCenterY)] ≈ [0, -(height * 26/40)]
    const iconAnchorX = Math.round(width / 2);
    const popupOffsetY = -Math.round(height * 26 / 40);

    return {
      options: {
        html,
        className: '',
        iconSize: [width, height],
        iconAnchor: [iconAnchorX, height],
        popupAnchor: [0, popupOffsetY],
      },
    };
  }

  // ── Circle (low-zoom dot) ─────────────────────────────────────────────────
  const diameter = width;
  const radius = diameter / 2;
  const iconInner = diameter - 8;

  const iconHtml = showIcon ? iconSvgPath(type, iconInner) : '';

  // SAFETY: color comes from MARKER_COLORS constant (hex literals). label is the
  // user-supplied waypoint name — it is HTML-escaped below before insertion.
  const labelHtml =
    showLabel && label
      ? `<span style="
          position:absolute;
          top:${diameter + 3}px;
          left:50%;
          transform:translateX(-50%);
          white-space:nowrap;
          font-family:'DM Sans',system-ui,sans-serif;
          font-size:10px;
          font-weight:600;
          color:#f0ede8;
          background:rgba(28,26,23,0.85);
          padding:1px 5px;
          border-radius:3px;
          pointer-events:none;
          box-shadow:0 1px 3px rgba(0,0,0,0.5);
        ">${label}</span>`
      : '';

  const safeLabelHtml = labelHtml.replace(/<span([^>]*)>(.+?)<\/span>/, (_, attrs, text) => {
    const escaped = (text as string).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<span${attrs}>${escaped}</span>`;
  });

  const html = `<div style="
    position:relative;
    width:${diameter}px;
    height:${diameter}px;
    border-radius:50%;
    background:${color};
    border:2px solid rgba(255,255,255,0.85);
    box-shadow:0 2px 6px rgba(0,0,0,0.4);
    filter:drop-shadow(0 1px 3px rgba(0,0,0,0.6));
    display:flex;
    align-items:center;
    justify-content:center;
    transition:width 0.15s ease,height 0.15s ease;
  ">${iconHtml}${safeLabelHtml}</div>`;

  return {
    options: {
      html,
      className: '',
      iconSize: [diameter, diameter],
      iconAnchor: [radius, radius],
      popupAnchor: [0, -radius - 4],
    },
  };
}

/**
 * Creates a live HTMLElement for use as a MapLibre GL marker element.
 * This is the MapLibre equivalent of createWaypointDivIcon.
 */
export function createMarkerElement(
  type: WaypointType,
  zoom: number,
  label?: string,
): HTMLElement {
  const { options } = createWaypointDivIcon(type, zoom, label);
  const wrapper = document.createElement('div');
  wrapper.innerHTML = options.html;
  // SAFETY: firstElementChild always exists since html always contains a root <div>
  return wrapper.firstElementChild as HTMLElement;
}
